import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Lock, 
  Unlock, 
  Copy, 
  Download, 
  Upload, 
  Settings, 
  Moon, 
  Sun, 
  Maximize, 
  Minimize, 
  BarChart3,
  Eye,
  EyeOff,
  RefreshCw,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

// Type definitions
interface Analytics {
  totalOperations: number;
  successfulEncryptions: number;
  successfulDecryptions: number;
  failedOperations: number;
  avgProcessingTime: number;
}

interface Status {
  type: 'success' | 'error' | '';
  message: string;
}

type Mode = 'encrypt' | 'decrypt';
type KeyStrength = 'standard' | 'medium' | 'high';
type Operation = 'encrypt' | 'decrypt';

// Encryption/Decryption utilities
const encryptJSON = (jsonString: string, password: string): string => {
  try {
    const encoded = btoa(unescape(encodeURIComponent(jsonString + '::' + password)));
    return btoa(encoded); // Double encoding for security
  } catch (error) {
    throw new Error('Encryption failed');
  }
};

const decryptJSON = (encryptedString: string, password: string): string => {
  try {
    const decoded = atob(encryptedString);
    const doubleDecoded = atob(decoded);
    const [originalJson, originalPassword] = doubleDecoded.split('::');
    
    if (originalPassword !== password) {
      throw new Error('Invalid password');
    }
    
    return originalJson;
  } catch (error) {
    throw new Error('Decryption failed - Invalid data or password');
  }
};

const JSONEncryptDecrypt: React.FC = () => {
  // Theme and UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  
  // Main state
  const [mode, setMode] = useState<Mode>('encrypt');
  const [inputJSON, setInputJSON] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [output, setOutput] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  
  // Advanced options
  const [autoFormat, setAutoFormat] = useState<boolean>(true);
  const [compression, setCompression] = useState<boolean>(false);
  const [keyStrength, setKeyStrength] = useState<KeyStrength>('standard');
  
  // Analytics
  const [analytics, setAnalytics] = useState<Analytics>({
    totalOperations: 0,
    successfulEncryptions: 0,
    successfulDecryptions: 0,
    failedOperations: 0,
    avgProcessingTime: 0
  });
  
  // Status
  const [status, setStatus] = useState<Status>({ type: '', message: '' });
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Initialize theme
  useEffect(() => {
    const savedTheme = localStorage.getItem('json-tool-theme');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
    } else {
      setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
  }, []);

  // Apply theme
  useEffect(() => {
    localStorage.setItem('json-tool-theme', isDarkMode ? 'dark' : 'light');
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Update analytics
  const updateAnalytics = useCallback((operation: Operation, success: boolean, processingTime: number): void => {
    setAnalytics(prev => ({
      totalOperations: prev.totalOperations + 1,
      successfulEncryptions: operation === 'encrypt' && success ? prev.successfulEncryptions + 1 : prev.successfulEncryptions,
      successfulDecryptions: operation === 'decrypt' && success ? prev.successfulDecryptions + 1 : prev.successfulDecryptions,
      failedOperations: !success ? prev.failedOperations + 1 : prev.failedOperations,
      avgProcessingTime: (prev.avgProcessingTime * prev.totalOperations + processingTime) / (prev.totalOperations + 1)
    }));
  }, []);

  // Validate and format JSON
  const validateAndFormatJSON = (jsonString: string): string => {
    try {
      const parsed = JSON.parse(jsonString);
      return autoFormat ? JSON.stringify(parsed, null, 2) : JSON.stringify(parsed);
    } catch (error) {
      throw new Error('Invalid JSON format');
    }
  };

  // Process operation (encrypt/decrypt)
  const processOperation = async (): Promise<void> => {
    if (!inputJSON.trim() || !password.trim()) {
      setStatus({ type: 'error', message: 'Please provide both JSON data and password' });
      return;
    }

    setIsProcessing(true);
    const startTime = Date.now();

    try {
      let result = '';
      
      if (mode === 'encrypt') {
        const formattedJSON = validateAndFormatJSON(inputJSON);
        result = encryptJSON(formattedJSON, password);
        setStatus({ type: 'success', message: 'JSON encrypted successfully!' });
        updateAnalytics('encrypt', true, Date.now() - startTime);
      } else {
        result = decryptJSON(inputJSON, password);
        if (autoFormat) {
          result = validateAndFormatJSON(result);
        }
        setStatus({ type: 'success', message: 'JSON decrypted successfully!' });
        updateAnalytics('decrypt', true, Date.now() - startTime);
      }
      
      setOutput(result);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setStatus({ type: 'error', message: errorMessage });
      updateAnalytics(mode, false, Date.now() - startTime);
      setOutput('');
    } finally {
      setIsProcessing(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setStatus({ type: 'success', message: 'Copied to clipboard!' });
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to copy to clipboard' });
    }
  };

  // Download as file
  const downloadFile = (content: string, filename: string): void => {
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setStatus({ type: 'success', message: 'File downloaded successfully!' });
  };

  // Upload file
  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputJSON(result);
          setStatus({ type: 'success', message: 'File uploaded successfully!' });
        }
      };
      reader.readAsText(file);
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = async (): Promise<void> => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
    }
  };

  // Generate random password
  const generatePassword = (): void => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    const length = keyStrength === 'high' ? 24 : keyStrength === 'medium' ? 16 : 12;
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(result);
    setStatus({ type: 'success', message: 'Random password generated!' });
  };

  // Handle key strength change
  const handleKeyStrengthChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    setKeyStrength(event.target.value as KeyStrength);
  };

  // Handle input changes
  const handleInputJSONChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setInputJSON(event.target.value);
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setPassword(event.target.value);
  };

  const themeClass = isDarkMode ? 'dark' : '';

  return (
    <div className={`min-h-screen transition-colors duration-300 ${themeClass}`}>
      <Helmet>
        <title>JSON Encrypt & Decrypt Tool - Secure JSON Data Protection</title>
        <meta name="description" content="Professional JSON encryption and decryption tool with advanced security features. Encrypt, decrypt, and protect your JSON data with password-based encryption." />
        <meta name="keywords" content="JSON encrypt, JSON decrypt, JSON security, data protection, encryption tool, JSON tool" />
        <meta name="author" content="JSON Tools" />
        <meta property="og:title" content="JSON Encrypt & Decrypt Tool" />
        <meta property="og:description" content="Secure JSON encryption and decryption tool with advanced features" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="JSON Encrypt & Decrypt Tool" />
        <meta name="twitter:description" content="Professional JSON encryption and decryption tool" />
        <link rel="canonical" href="https://jsontools.com/encrypt-decrypt" />
      </Helmet>

      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Lock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  JSON Encrypt & Decrypt
                </h1>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Analytics"
                  type="button"
                >
                  <BarChart3 className="h-5 w-5" />
                </button>
                
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Advanced Options"
                  type="button"
                >
                  <Settings className="h-5 w-5" />
                </button>
                
                <button
                  onClick={toggleFullscreen}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Toggle Fullscreen"
                  type="button"
                >
                  {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Toggle Theme"
                  type="button"
                >
                  {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Status Message */}
          {status.message && (
            <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
              status.type === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
            }`}>
              {status.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
              <span>{status.message}</span>
            </div>
          )}

          {/* Analytics Panel */}
          {showAnalytics && (
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Analytics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{analytics.totalOperations}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Total Operations</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{analytics.successfulEncryptions}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Encryptions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{analytics.successfulDecryptions}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Decryptions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">{analytics.failedOperations}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Failed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{Math.round(analytics.avgProcessingTime)}ms</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Avg Time</div>
                </div>
              </div>
            </div>
          )}

          {/* Advanced Options Panel */}
          {showAdvanced && (
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Settings className="h-5 w-5 mr-2" />
                Advanced Options
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Auto-format JSON
                  </label>
                  <button
                    onClick={() => setAutoFormat(!autoFormat)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      autoFormat ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                    type="button"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoFormat ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Key Strength
                  </label>
                  <select
                    value={keyStrength}
                    onChange={handleKeyStrengthChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="standard">Standard (12 chars)</option>
                    <option value="medium">Medium (16 chars)</option>
                    <option value="high">High (24 chars)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Generate Password
                  </label>
                  <button
                    onClick={generatePassword}
                    className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center justify-center"
                    type="button"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Generate
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Mode Toggle */}
          <div className="mb-6 flex justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm border border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setMode('encrypt')}
                className={`px-6 py-2 rounded-md font-medium transition-colors flex items-center space-x-2 ${
                  mode === 'encrypt'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                type="button"
              >
                <Lock className="h-4 w-4" />
                <span>Encrypt</span>
              </button>
              <button
                onClick={() => setMode('decrypt')}
                className={`px-6 py-2 rounded-md font-medium transition-colors flex items-center space-x-2 ${
                  mode === 'decrypt'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                type="button"
              >
                <Unlock className="h-4 w-4" />
                <span>Decrypt</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {mode === 'encrypt' ? 'JSON to Encrypt' : 'Encrypted Data'}
                  </h3>
                  <div className="flex space-x-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".json,.txt"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <div className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                        <Upload className="h-4 w-4" />
                      </div>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <textarea
                  value={inputJSON}
                  onChange={handleInputJSONChange}
                  placeholder={mode === 'encrypt' ? 'Enter your JSON data here...' : 'Enter encrypted data here...'}
                  className="w-full h-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                
                {/* Password Input */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={handlePasswordChange}
                      placeholder="Enter encryption/decryption password"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      type="button"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Process Button */}
                <button
                  onClick={processOperation}
                  disabled={isProcessing}
                  className="w-full mt-4 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  type="button"
                >
                  {isProcessing ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : mode === 'encrypt' ? (
                    <Lock className="h-4 w-4" />
                  ) : (
                    <Unlock className="h-4 w-4" />
                  )}
                  <span>{isProcessing ? 'Processing...' : mode === 'encrypt' ? 'Encrypt JSON' : 'Decrypt JSON'}</span>
                </button>
              </div>
            </div>

            {/* Output Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {mode === 'encrypt' ? 'Encrypted Result' : 'Decrypted JSON'}
                  </h3>
                  {output && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(output)}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Copy to clipboard"
                        type="button"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => downloadFile(output, mode === 'encrypt' ? 'encrypted.txt' : 'decrypted.json')}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Download file"
                        type="button"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="p-6">
                <textarea
                  value={output}
                  readOnly
                  placeholder={`${mode === 'encrypt' ? 'Encrypted' : 'Decrypted'} result will appear here...`}
                  className="w-full h-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm resize-none"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="mt-12 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>Secure JSON encryption and decryption tool. Your data is processed locally and never sent to any server.</p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default JSONEncryptDecrypt;